import { FormField } from '@frontapp/ui-kit';
import {
  ErrorMessage,
  Field,
  type FieldMetaProps,
  type FieldInputProps as FormikFieldInputProps,
  type FieldProps as FormikFieldProps,
  type FormikProps,
  getIn,
  useFormikContext,
} from 'formik';
import { type ReactNode, createElement } from 'react';

type EssentialFieldProps<T> = {
  name: string;
  component: React.ComponentType<T>;
  disabled?: boolean;
} & Omit<T, 'field' | 'form' | 'meta'>;

export type FieldInputProps<V = any> = Omit<
  FormikFieldInputProps<V>,
  'onChange' | 'onBlur'
> & {
  onChange: (newValue: V) => void;
};

export interface FieldProps<V = any, FormValues = any> {
  field: FieldInputProps<V>;
  form: FormikProps<FormValues>;
  meta: FieldMetaProps<V>;
  disabled?: boolean;
}

type WrapperProps = {
  label: ReactNode;
  hint?: ReactNode;
};

export function FieldWrapper<T>({
  label,
  name,
  hint,
  component,
  disabled,
  ...props
}: EssentialFieldProps<T> & WrapperProps) {
  const { errors } = useFormikContext();

  return (
    <div style={{ margin: '15px 0' }}>
      <FormField
        label={label as any}
        hint={hint as any}
        errorMessage={getIn(errors, name)}
      >
        <Field name={name}>
          {(fieldProps: FormikFieldProps<any>) =>
            createElement(
              component as any,
              {
                ...props,
                ...fieldProps,
                field: {
                  ...fieldProps.field,
                  onChange: (newValue: any) =>
                    fieldProps.form.setFieldValue(name, newValue),
                },
                disabled,
              } as any,
            )
          }
        </Field>
      </FormField>
    </div>
  );
}
